<template>
  <div class="register-page">
    <TheAppBar />

    <v-content transition="out-in">
      <v-container>
        <v-row class="register-page__row">
          <v-col class="register-page__form-col">
            <h5 class="register-page__form-label">Register as a client</h5>

            <v-form
              ref="registerForm"
              class="register-form"
              v-model="isFormValid"
              @submit.prevent="submitForm"
            >
              <v-row>
                <v-col class="register-form__col">
                  <v-text-field
                    v-model="client.first_name"
                    class="form-field"
                    :rules="[rules.required]"
                    dense
                    label="First name*"
                    placeholder="Type name..."
                  />
                </v-col>
                <v-col class="register-form__col">
                  <v-text-field
                    v-model="client.last_name"
                    class="form-field"
                    :rules="[rules.required]"
                    dense
                    label="Last name*"
                    placeholder="Type name..."
                  />
                </v-col>
              </v-row>

              <v-row>
                <v-col class="register-form__col">
                  <v-text-field
                    v-model="client.password"
                    class="form-field"
                    :rules="[rules.required, rules.password]"
                    dense
                    type="password"
                    label="Password*"
                    placeholder="Type password..."
                  />
                </v-col>
                <v-col class="register-form__col">
                  <v-text-field
                    v-model="client.password_repeat"
                    class="form-field"
                    :rules="[rules.required, equalPasswordRule]"
                    dense
                    type="password"
                    label="Repeat password*"
                    placeholder="Type password..."
                  />
                </v-col>
              </v-row>

              <v-row>
                <v-col class="register-form__col">
                  <v-text-field
                    v-model="client.company_name"
                    class="form-field"
                    :rules="[rules.required]"
                    dense
                    label="Company name*"
                    placeholder="Type name..."
                  />
                </v-col>
                <v-col class="register-form__col">
                  <v-text-field
                    v-model="client.email"
                    class="form-field"
                    :rules="[rules.required, rules.email]"
                    dense
                    validate-on-blur
                    label="Email Address*"
                    placeholder="Type email"
                  />
                </v-col>
              </v-row>

              <v-row>
                <v-col class="register-form__col col-6">
                  <v-select
                    v-model="client.company_size"
                    class="form-field"
                    :items="companySizes"
                    item-value="value"
                    item-text="name"
                    item-color="blue"
                    :rules="[rules.required]"
                    dense
                    label="Company size*"
                    placeholder="Choose size"
                  />
                </v-col>
              </v-row>

              <v-row>
                <v-col class="register-form__col">
                  <div class="accept-terms">
                    <v-checkbox
                      v-model="isFormAccepted"
                      class="accept-terms__checkbox"
                      color="blue"
                      dense
                      hide-details
                    >
                      <template #label>
                        <span class="accept-terms__acceptance">
                          Please confirm that you agree to our
                        </span>
                      </template>
                    </v-checkbox>
                    <a
                      class="accept-terms__link ml-1"
                      target="_blank"
                      href="https://applover.com/wp-content/uploads/2020/07/privacy-policy-Applover-06.2020.pdf"
                    >
                      <b>terms & conditions</b>
                    </a>
                  </div>
                </v-col>
              </v-row>

              <v-row class="mt-3">
                <v-col class="register-form__col">
                  <v-btn
                    color="blue"
                    class="white--text fw-400"
                    large
                    :disabled="!isFormAccepted"
                    :min-width="255"
                    :loading="isSubmitProccesing"
                    type="submit"
                  >
                    CREATE ACCOUNT
                  </v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-col>

          <v-col>
            <h1 class="register-page__title">Use Machine Learning for dev<br>teams subscription</h1>
            <p class="register-page__text">
              - Perfect match<br>
              - 7-day trial<br>
              - Effortless employment<br>
              - Pre-scanned developers<br>
              - Equipment and workplace support
            </p>
            <img class="register-page__img"
              src="@/assets/img/interview_rmcf.svg"
              alt="Register image" />
          </v-col>
        </v-row>
      </v-container>
    </v-content>
  </div>
</template>

<script>
import TheAppBar from '@/components/TheAppBar.vue';
import validationRules from '@/helpers/validators';
import { companySizes } from '@/helpers/placeholderData';

const rules = validationRules;

export default {
  metaInfo: {
    title: 'Register',
  },

  components: {
    TheAppBar,
  },

  data() {
    return {
      rules,
      companySizes,
      isFormValid: true,
      isFormAccepted: true,
      isSubmitProccesing: false,
      client: {
        first_name: '',
        last_name: '',
        company_name: '',
        email: '',
        password: '',
        password_repeat: '',
        company_size: '',
      },
    };
  },

  computed: {
    equalPasswordRule() {
      return this.client.password_repeat === this.client.password || 'Passwords don`t match';
    },
  },

  methods: {
    submitForm() {
      this.$refs.registerForm.validate();

      if (this.isFormValid) {
        const { client } = this;
        this.isSubmitProccesing = true;

        this.$store.dispatch('auth/registerClient', client)
          .then(() => {
            this.$router.push({ name: 'projects' });
          })
          .catch(() => {
            this.isSubmitProccesing = false;
          });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.register-page {
  &__row {
    flex-direction: column-reverse;

    @include tablet-xl {
      margin-top: 24px;
      flex-direction: row;
    }

    @include desktop {
      margin-top: 36px;
    }
  }

  &__form-col {
    padding-right: 12px;

    @include desktop {
      padding-right: 80px;
    }
  }

  &__form-label {
    font-size: 12px;
    font-weight: 600;
    padding-bottom: 8px;
    margin-bottom: 16px;
    border-bottom: 1px solid #E2E6EA;
    color: $color--primary;
  }

  &__title {
    font-size: 24px;
    margin-bottom: 12px;
    text-align: center;
    color: $color--primary;

    @include tablet-xl {
      font-size: 28px;
      line-height: 36px;
      text-align: left;
    }
  }

  &__text {
    line-height: 28px;
    margin-bottom: 32px;
    @include desktop {
      margin-bottom: 46px;
    }
  }

  &__img {
    max-width: 100%;
  }
}

.register-form {
  &__col {
    padding: 0 8px 8px;
  }

  &__acceptance {
    color: $color--primary;
    font-size: 12px;

    .v-label {
      font-size: 12px;
    }
  }
}
.accept-terms {
  color: $color--primary;
  font-size: 12px;
  line-height: 14px;
  display: flex;
  align-items: center;

  &__checkbox {
    margin-top: 0;
    padding-top: 0px;
  }

  &__acceptance {
    color: $color--primary;
    font-size: 12px;
    line-height: 22px;
  }

  &__link {
    line-height: 24px;
  }
}
</style>
